// MainCanvas.jsx (updated container3D only)
import { Suspense, useContext, useEffect, useRef } from "react";
import { FormControlLabel, FormGroup, IconButton, Switch } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import "../App.css";
import CanvasContainer from "../Canvas.js";
import { CustomLoader } from "./Common/CustomLoader.js";
import stateContext from "../Context/StateContext.js";
import FontSize from "./FontSize/FontSize.js";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client.js";
import { AttachmentSize } from "./TabsContent/AttachmentSizes.js";
import { useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
} from "@mui/material";
import { ExpandLess, InfoOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

const MainCanvas = () => {
    const globalState = useContext(stateContext);
    const containerRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const profit = useQuery({
        queryKey: ["profit"],
        queryFn: () => apiClient.get("/profits"),
    });

    useEffect(() => {
        if (profit.data) {
            globalState.setProfit(profit.data.data[0].percentage);
        }
    }, [profit.data]);

    const chainCharmData = useQuery({
        queryKey: ["charm-types"],
        queryFn: () => apiClient.get("/charm-types"),
    });

    useEffect(() => {
        if (chainCharmData.data) {
            globalState.setChainCharmData(chainCharmData.data.data);
        }
    }, [chainCharmData.data]);

    const openHome = () => {
        window.open("https://haref.net/", "_blank");
    };

    const handleChange = (event) => {
        globalState.setShowDimension(event.target.checked);
    };

    const isArabic = globalState.selectedFontLanguage === "Arabic";

    // const area = globalState.plateArea.toFixed(2);
    // const vol = (
    //     (globalState.plateArea * globalState.fontThickness) /
    //     10
    // ).toFixed(2);
    // const plateWeight = globalState.textWeight.toFixed(2);

    const labels = {
        area: isArabic ? "منطقة" : "Area",
        volume: isArabic ? "حجم" : "Volume",
        weight: isArabic ? "وزن" : "Weight",
        dimension: isArabic ? "البعد" : "Dimension",
        cm: isArabic ? "سنتيمتر" : "cm",
        cm2: isArabic ? "سم٢" : "cm²",
        cm3: isArabic ? "سم٣" : "cm³",
        gm: isArabic ? "جرام" : "gm",
    };

    const not = "الأوزان والأبعاد تقريبية وقد تزيد أو تنقص بشكل بسيط.";

    const handleCloseFullscreen = () => {
        globalState.setIsMobileFullscreen(false);
        globalState.setAddDotDiamond(false);
    };

    // Handle window resize to close fullscreen if switching from mobile to desktop
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth <= 1367;
            if (!isMobile && globalState.isMobileFullscreen) {
                globalState.setIsMobileFullscreen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [globalState]);

    return (
        <div className="main__canvas">
            <Suspense fallback={<CustomLoader />}>
                <>
                    <div
                        className={`container3D ${globalState.isMobileFullscreen ? 'mobile-fullscreen' : ''}`}
                        id="container3D"
                        ref={containerRef}
                        style={{ 
                            position: "relative", 
                            gap: "20px",
                            ...(globalState.isMobileFullscreen && {
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                zIndex: 9999,
                                background: "#000"
                            })
                        }} // ensure container is the positioned parent
                    >
                        <CanvasContainer />

                        {/* Close button for mobile fullscreen when diamonds are added */}
                        {globalState.isMobileFullscreen && globalState.diamondPosData.length > 0 && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "20px",
                                    right: "20px",
                                    zIndex: 10000,
                                }}
                            >
                                <IconButton
                                    onClick={handleCloseFullscreen}
                                    sx={{
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        color: "#000",
                                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                                        padding: "12px",
                                        boxShadow: "0 2px 10px rgba(0,0,0,0.3)"
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        )}

                        <div
                            className="desktop-view"
                            style={{ background: "red" }}
                        >
                            {/* Original divs */}
                            <div
                                className="canvas-description-wrapper"
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: "10%", // moved up to make space for accordion below
                                    transform: "translateX(-50%)",
                                    maxWidth: "750px", // constrain width like your 2nd image
                                    width: "calc(100% - 230px)", // responsive: inset a bit from container sides
                                    padding: "10px 16px",
                                    textAlign: isArabic ? "right" : "center",
                                    direction: isArabic ? "rtl" : "ltr",
                                    fontSize: 14,
                                    fontWeight: isArabic ? 600 : 400,
                                    color: "var(--text-color, #222)",
                                    background: "white",
                                    zIndex: 20,
                                    boxSizing: "border-box",
                                    borderRadius: "8px",
                                    // optionally add subtle text shadow for contrast:
                                    textShadow:
                                        "0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(0,0,0,0.25)",
                                    whiteSpace: "pre-wrap",
                                    marginBottom: "15px",
                                }}
                            >
                                <div
                                    className="canvas-description"
                                    style={{
                                        background: "transparent",
                                    }}
                                >
                                    <p style={{ margin: 0 }}>
                                        {globalState.description}
                                    </p>
                                    <p
                                        style={{
                                            background: "transparent",
                                            color: "black",
                                            fontSize: "15px",
                                            // fontWeight: "bold",
                                        }}
                                    >
                                        {not}
                                    </p>
                                </div>
                            </div>

                            <div
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: "4%", // lower than description so accordion sits below it
                                    transform: "translateX(-50%)",
                                    width: "calc(100% - 380px)",
                                    maxWidth: "750px",
                                    zIndex: 25,
                                    boxSizing: "border-box",
                                }}
                            >
                                <FontAccordion
                                    // pass description so accordion can show it underneath details when expanded
                                    isArabic={isArabic}
                                    // pass details as JSX (6 sample items in 3x2)
                                    details={
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: "12px 24px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic ? "الخط" : "Font"}
                                                </div>
                                                {globalState.selectedFontData && (
                                                    <div
                                                        style={{
                                                            color: "#fff",
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {globalState
                                                            .selectedFontData
                                                            ?.arabicName ??
                                                            globalState
                                                                .selectedFontData
                                                                ?.name}
                                                    </div>
                                                )}
                                            </div>

                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? "المعدن/العيار"
                                                        : "Metal/Carat"}
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#fff",
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {globalState
                                                        .selectedMetalData
                                                        ?.arabicName ??
                                                        globalState
                                                            .selectedMetalData
                                                            ?.name}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? "السمك"
                                                        : "Thickness"}
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#fff",
                                                        fontSize: 14,
                                                        direction: "rtl", // whole block RTL
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            direction: "ltr",
                                                            unicodeBidi:
                                                                "isolate",
                                                        }}
                                                    >
                                                        {
                                                            globalState.fontThickness
                                                        }{" "}
                                                        mm
                                                    </span>
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? "السلسلة"
                                                        : "Chain"}
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#fff",
                                                        fontSize: 14,
                                                        backgroundColor: "#000",
                                                        width: "200px", // Approximate width to match the image
                                                        lineHeight: "1.5", // Adjusts spacing between lines
                                                    }}
                                                >
                                                    نوع السلسلة: 40 مم السلاسل
                                                    كلها
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? "الوزن التقريبي"
                                                        : "Approx. weight"}
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#fff",
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? `${globalState.pendantWeight} جرام`
                                                        : `${globalState.pendantWeight} gm`}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    direction: isArabic
                                                        ? "rtl"
                                                        : "ltr",
                                                    textAlign: isArabic
                                                        ? "right"
                                                        : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        color: "#bdbdbd",
                                                        fontSize: 12,
                                                        fontWeight: isArabic
                                                            ? 600
                                                            : 500,
                                                    }}
                                                >
                                                    {isArabic
                                                        ? "الأبعاد (الطول × العرض × السُمك):"
                                                        : "Dimensions"}
                                                </div>
                                                <div
                                                    style={{ direction: "rtl" }}
                                                >
                                                    <span dir="ltr">
                                                        {(
                                                            globalState
                                                                .plateSize
                                                                ?.width / 10
                                                        ).toFixed(2)}{" "}
                                                        x{" "}
                                                        {(
                                                            globalState
                                                                .plateSize
                                                                ?.height / 10
                                                        ).toFixed(2)}{" "}
                                                        x{" "}
                                                        {(
                                                            globalState.fontThickness *
                                                            0.1
                                                        ).toFixed(3)}{" "}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                        </div>

                        <div
                            className="mobile-view"
                            style={{
                                position: "absolute",
                                right: "10px",
                                top: "0%",
                                display: "flex",
                                gap: "5px",
                                zIndex: 20,
                            }}
                        >
                            {isDropdownOpen && (
                                <div
                                    style={{
                                        background: "white",
                                        height: "100%",
                                        borderRadius: "8px",
                                        marginLeft: "15px",
                                    }}
                                >
                                    {/* Original divs */}
                                    <div
                                        className="canvas-description-wrapper"
                                        style={{
                                            top: "-9%", // moved up to make space for accordion below
                                            minWidth: "100px",
                                            maxWidth: "350px",
                                            right: "0%",
                                            padding: "5px 8px",
                                            maxHeight: "150px",
                                            overflowY: "auto",
                                            textAlign: isArabic
                                                ? "right"
                                                : "center",
                                            direction: isArabic ? "rtl" : "ltr",
                                            fontSize: 14,
                                            fontWeight: isArabic ? 600 : 400,
                                            color: "var(--text-color, #222)",
                                            zIndex: 20,
                                            boxSizing: "border-box",
                                            borderRadius: "8px",
                                            // optionally add subtle text shadow for contrast:
                                            textShadow:
                                                "0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(0,0,0,0.25)",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        <div
                                            className="canvas-description"
                                            style={{
                                                background: "transparent",
                                            }}
                                        >
                                            <p style={{ margin: 0 }}>
                                                {globalState.description}
                                            </p>
                                            <p
                                                style={{
                                                    background: "transparent",
                                                    color: "black",
                                                    fontSize: "15px",
                                                    marginBottom: "0px",

                                                    // fontWeight: "bold",
                                                }}
                                            >
                                                {not}
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            minWidth: "100px",
                                            width: "calc(100% - 12px)",
                                            zIndex: 25,
                                            boxSizing: "border-box",
                                            margin: "20px 10px 10px 7px",
                                        }}
                                    >
                                        <FontAccordion
                                            // pass description so accordion can show it underneath details when expanded
                                            isArabic={isArabic}
                                            // pass details as JSX (6 sample items in 3x2)
                                            details={
                                                <div
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns:
                                                            "1fr 1fr",
                                                        gap: "12px 24px",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "الخط"
                                                                : "Font"}
                                                        </div>
                                                        {globalState.selectedFontData && (
                                                            <div
                                                                style={{
                                                                    color: "#fff",
                                                                    fontSize: 14,
                                                                }}
                                                            >
                                                                {globalState
                                                                    .selectedFontData
                                                                    ?.arabicName ??
                                                                    globalState
                                                                        .selectedFontData
                                                                        ?.name}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "المعدن/العيار"
                                                                : "Metal/Carat"}
                                                        </div>
                                                        <div
                                                            style={{
                                                                color: "#fff",
                                                                fontSize: 14,
                                                            }}
                                                        >
                                                            {globalState
                                                                .selectedMetalData
                                                                ?.arabicName ??
                                                                globalState
                                                                    .selectedMetalData
                                                                    ?.name}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "السمك"
                                                                : "Thickness"}
                                                        </div>
                                                        <div
                                                            style={{
                                                                color: "#fff",
                                                                fontSize: 14,
                                                                direction:
                                                                    "rtl", // whole block RTL
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    direction:
                                                                        "ltr",
                                                                    unicodeBidi:
                                                                        "isolate",
                                                                }}
                                                            >
                                                                {
                                                                    globalState.fontThickness
                                                                }{" "}
                                                                mm
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "السلسلة"
                                                                : "Chain"}
                                                        </div>
                                                        <div
                                                            style={{
                                                                color: "#fff",
                                                                fontSize: 14,
                                                                backgroundColor:
                                                                    "#000",
                                                                flexWrap:
                                                                    "wrap",
                                                                width: "120px", // Approximate width to
                                                                // match the image
                                                                lineHeight:
                                                                    "1.5", // Adjusts spacing between lines
                                                            }}
                                                        >
                                                            نوع السلسلة: 40 مم
                                                            السلاسل كلها
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "الوزن التقريبي"
                                                                : "Approx. weight"}
                                                        </div>
                                                        <div
                                                            style={{
                                                                color: "#fff",
                                                                fontSize: 14,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? `${globalState.pendantWeight} جرام`
                                                                : `${globalState.pendantWeight} gm`}
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            direction: isArabic
                                                                ? "rtl"
                                                                : "ltr",
                                                            textAlign: isArabic
                                                                ? "right"
                                                                : "left",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#bdbdbd",
                                                                fontSize: 12,
                                                                fontWeight:
                                                                    isArabic
                                                                        ? 600
                                                                        : 500,
                                                            }}
                                                        >
                                                            {isArabic
                                                                ? "الأبعاد (الطول × العرض × السُمك):"
                                                                : "Dimensions"}
                                                        </div>
                                                        <div
                                                            style={{
                                                                direction:
                                                                    "rtl",
                                                            }}
                                                        >
                                                            <span dir="ltr">
                                                                {(
                                                                    globalState
                                                                        .plateSize
                                                                        ?.width /
                                                                    10
                                                                ).toFixed(
                                                                    2
                                                                )}{" "}
                                                                x{" "}
                                                                {(
                                                                    globalState
                                                                        .plateSize
                                                                        ?.height /
                                                                    10
                                                                ).toFixed(
                                                                    2
                                                                )}{" "}
                                                                x{" "}
                                                                {(
                                                                    globalState.fontThickness *
                                                                    0.1
                                                                ).toFixed(
                                                                    3
                                                                )}{" "}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {/* <button
                                onClick={() =>
                                    setIsDropdownOpen(!isDropdownOpen)
                                }
                                style={{
                                    height: "fit-content",
                                    background: "#fff",
                                    border: "1px solid #ccc",
                                    borderRadius: "8px",
                                    padding: "5px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <InfoOutlined />
                            </button> */}
                        </div>
                    </div>

                    {/* Right-side controls / panel */}
                    <IconButton
                        onClick={() =>
                            window.open("https://haref.net/", "_blank")
                        }
                        className="home-btn"
                        sx={{
                            backgroundColor: "primary.main",
                            color: "secondary.main",
                            "&:hover": { backgroundColor: "primary.light" },
                        }}
                    >
                        <HomeOutlinedIcon />
                    </IconButton>

                    {/* Dimension toggle & other UI (kept outside canvas) */}
                    <div className="dimension-box">
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={globalState.showDimension}
                                        onChange={(e) =>
                                            globalState.setShowDimension(
                                                e.target.checked
                                            )
                                        }
                                    />
                                }
                                label={`${labels.dimension} (${labels.cm})`}
                            />
                        </FormGroup>
                    </div>

                    <FontSize />
                    <AttachmentSize />
                </>
            </Suspense>
        </div>
    );
};
export default MainCanvas;

// FontAccordion.jsx

export function FontAccordion({ description, details, isArabic = false }) {
    const [expanded, setExpanded] = useState(false);

    // header text (Arabic if selected)
    const headerText = isArabic ? "وصف المنتج" : "Product Details";

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                // background:
                //     "linear-gradient(180deg, rgba(124, 124, 124, 0.95), rgba(124, 124, 124, 0.95))",
                background: "#000",
                color: "#fff",
                boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandLess sx={{ color: "#fff" }} />}
                sx={{
                    paddingY: 1,
                    paddingX: 2,
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": { margin: 0 },
                }}
            >
                <Typography
                    sx={{
                        fontWeight: isArabic ? 700 : 600,
                        fontSize: 15,
                        direction: isArabic ? "rtl" : "ltr",
                        textAlign: isArabic ? "right" : "left",
                        width: "100%",
                    }}
                >
                    {headerText}
                </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: 2 }}>
                {/* details JSX (passed from parent) */}
                <Box sx={{ marginBottom: 1 }}>{details}</Box>

                {/* description displayed after details (kept for completeness) */}
                <Typography
                    sx={{
                        fontSize: 13,
                        color: "#ddd",
                        direction: isArabic ? "rtl" : "ltr",
                        textAlign: isArabic ? "right" : "left",
                    }}
                >
                    {description}
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
}
